import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  AlertTitle,
  CircularProgress,
  Autocomplete,
  IconButton,
  Divider,
} from '@mui/material';
import { Close, Inventory2 } from '@mui/icons-material';
import { headerCellSx } from '../../components/common/pageKit';
import scmService, {
  PendingPoItem,
  PurchaseOrderOption,
  WarehouseOption,
  MatchTolerances,
} from '../../services/scmService';

/** DECIMAL columns arrive as strings over the wire (see scmService.ts header comment). */
const toNum = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const EPSILON = 0.005;
const todayISO = () => new Date().toISOString().slice(0, 10);

interface LineDraft {
  receiving: string;
  unitPrice: string;
  batchNumber: string;
  expiryDate: string;
}

const emptyLine: LineDraft = { receiving: '', unitPrice: '', batchNumber: '', expiryDate: '' };

interface GoodsReceiptFormDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const GoodsReceiptFormDialog: React.FC<GoodsReceiptFormDialogProps> = ({ open, onClose, onCreated }) => {
  const [pos, setPos] = useState<PurchaseOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [tolerances, setTolerances] = useState<MatchTolerances | null>(null);
  const [loadingRefData, setLoadingRefData] = useState(false);

  const [selectedPo, setSelectedPo] = useState<PurchaseOrderOption | null>(null);
  const [pendingItems, setPendingItems] = useState<PendingPoItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [lines, setLines] = useState<Record<number, LineDraft>>({});

  const [warehouseId, setWarehouseId] = useState('');
  const [receiptDate, setReceiptDate] = useState(todayISO());
  const [vendorDcNumber, setVendorDcNumber] = useState('');
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [ewayBillNumber, setEwayBillNumber] = useState('');
  const [remarks, setRemarks] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setSelectedPo(null);
    setPendingItems([]);
    setLines({});
    setWarehouseId('');
    setReceiptDate(todayISO());
    setVendorDcNumber('');
    setVehicleNumber('');
    setEwayBillNumber('');
    setRemarks('');
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    resetForm();
    setLoadingRefData(true);
    Promise.all([
      scmService.listPurchaseOrders(),
      scmService.listWarehouses(),
      scmService.getTolerances(),
    ])
      .then(([poRes, whRes, tolRes]) => {
        setPos(poRes.purchaseOrders.filter((po) => po.status !== 'CANCELLED'));
        setWarehouses(whRes.warehouses.filter((w) => Number(w.is_active) === 1 || w.is_active === true));
        setTolerances(tolRes.tolerances);
      })
      .catch((err: any) => setError(err.message || 'Failed to load reference data'))
      .finally(() => setLoadingRefData(false));
  }, [open, resetForm]);

  const handleSelectPo = (po: PurchaseOrderOption | null) => {
    setSelectedPo(po);
    setPendingItems([]);
    setLines({});
    setError(null);
    if (!po) return;

    setLoadingItems(true);
    scmService
      .getPendingPoItems(po.id)
      .then(({ items }) => {
        setPendingItems(items);
        const draft: Record<number, LineDraft> = {};
        items.forEach((item) => {
          draft[item.po_item_id] = { ...emptyLine };
        });
        setLines(draft);
      })
      .catch((err: any) => setError(err.message || 'Failed to load PO lines'))
      .finally(() => setLoadingItems(false));
  };

  const allowOverReceipt = tolerances ? Number(tolerances.allow_over_receipt) === 1 || tolerances.allow_over_receipt === true : false;

  const lineViolation = useCallback(
    (item: PendingPoItem): { overBy: number } | null => {
      const draft = lines[item.po_item_id];
      const receiving = toNum(draft?.receiving);
      if (receiving <= 0) return null;
      const ordered = toNum(item.ordered_quantity);
      const alreadyReceived = toNum(item.received_quantity);
      const cumulative = alreadyReceived + receiving;
      const overBy = cumulative - ordered;
      return overBy > EPSILON ? { overBy } : null;
    },
    [lines]
  );

  const activeLines = useMemo(
    () => pendingItems.filter((item) => toNum(lines[item.po_item_id]?.receiving) > 0),
    [pendingItems, lines]
  );

  const blockingViolation = useMemo(
    () => !allowOverReceipt && activeLines.some((item) => lineViolation(item) !== null),
    [activeLines, allowOverReceipt, lineViolation]
  );

  const canSubmit =
    !!selectedPo && !!warehouseId && !!receiptDate && activeLines.length > 0 && !blockingViolation && !submitting;

  const updateLine = (poItemId: number, patch: Partial<LineDraft>) => {
    setLines((prev) => ({ ...prev, [poItemId]: { ...prev[poItemId], ...patch } }));
  };

  const handleSubmit = async () => {
    if (!selectedPo || !warehouseId) return;
    setSubmitting(true);
    setError(null);
    try {
      const items = activeLines.map((item) => {
        const draft = lines[item.po_item_id];
        return {
          poItemId: item.po_item_id,
          receivedQuantity: toNum(draft.receiving),
          ...(draft.unitPrice.trim() ? { unitPrice: toNum(draft.unitPrice) } : {}),
          ...(draft.batchNumber.trim() ? { batchNumber: draft.batchNumber.trim() } : {}),
          ...(draft.expiryDate ? { expiryDate: draft.expiryDate } : {}),
        };
      });

      await scmService.createGoodsReceipt({
        poId: selectedPo.id,
        warehouseId: Number(warehouseId),
        receiptDate,
        vendorDcNumber: vendorDcNumber.trim() || null,
        vehicleNumber: vehicleNumber.trim() || null,
        ewayBillNumber: ewayBillNumber.trim() || null,
        remarks: remarks.trim() || null,
        items,
      });

      onCreated();
      onClose();
    } catch (err: any) {
      // The server's 409 over-receipt message already names the exact
      // quantities involved — surfaced verbatim rather than replaced with a
      // generic toast, since it is the actionable information here.
      setError(err.message || 'Failed to create goods receipt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Inventory2 fontSize="small" color="primary" />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Receive Goods Against a PO
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loadingRefData ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Autocomplete
                options={pos}
                sx={{ minWidth: 280, flex: 1 }}
                getOptionLabel={(o) => `${o.po_number} — ${o.vendor_name}`}
                value={selectedPo}
                onChange={(_, v) => handleSelectPo(v)}
                renderInput={(params) => <TextField {...params} label="Purchase Order *" size="small" />}
              />
              <TextField
                select
                size="small"
                label="Warehouse *"
                value={warehouseId}
                onChange={(e) => setWarehouseId(e.target.value)}
                sx={{ minWidth: 200 }}
              >
                {warehouses.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.warehouse_name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                type="date"
                label="Receipt Date *"
                value={receiptDate}
                onChange={(e) => setReceiptDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 170 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="Vendor DC Number"
                value={vendorDcNumber}
                onChange={(e) => setVendorDcNumber(e.target.value)}
                sx={{ minWidth: 180, flex: 1 }}
              />
              <TextField
                size="small"
                label="Vehicle Number"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                sx={{ minWidth: 160, flex: 1 }}
              />
              <TextField
                size="small"
                label="E-Way Bill Number"
                value={ewayBillNumber}
                onChange={(e) => setEwayBillNumber(e.target.value)}
                sx={{ minWidth: 160, flex: 1 }}
              />
            </Box>

            {selectedPo && (
              <>
                <Divider />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  PO Lines
                </Typography>

                {loadingItems ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : pendingItems.length === 0 ? (
                  <Alert severity="info">This PO has no lines, or every line is already fully received.</Alert>
                ) : (
                  <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={headerCellSx}>Product</TableCell>
                          <TableCell sx={headerCellSx} align="right">Ordered</TableCell>
                          <TableCell sx={headerCellSx} align="right">Already Received</TableCell>
                          <TableCell sx={headerCellSx} align="right">Pending</TableCell>
                          <TableCell sx={headerCellSx}>Receiving Now *</TableCell>
                          <TableCell sx={headerCellSx}>Unit Price</TableCell>
                          <TableCell sx={headerCellSx}>Batch</TableCell>
                          <TableCell sx={headerCellSx}>Expiry</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {pendingItems.map((item) => {
                          const draft = lines[item.po_item_id] || emptyLine;
                          const violation = lineViolation(item);
                          const pending = toNum(item.ordered_quantity) - toNum(item.received_quantity);
                          return (
                            <TableRow key={item.po_item_id} hover>
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {item.product_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {item.product_code} · {item.unit_of_measure}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {toNum(item.ordered_quantity)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {toNum(item.received_quantity)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {Math.max(pending, 0)}
                              </TableCell>
                              <TableCell sx={{ minWidth: 130 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  value={draft.receiving}
                                  onChange={(e) => updateLine(item.po_item_id, { receiving: e.target.value })}
                                  error={!!violation && !allowOverReceipt}
                                  helperText={
                                    violation
                                      ? allowOverReceipt
                                        ? `Over-receipt by ${violation.overBy.toFixed(2)} — allowed by tolerance`
                                        : `Exceeds ordered qty by ${violation.overBy.toFixed(2)}`
                                      : undefined
                                  }
                                  inputProps={{ min: 0, step: '0.01' }}
                                  sx={{ minWidth: 120 }}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 110 }}>
                                <TextField
                                  size="small"
                                  type="number"
                                  placeholder={String(toNum(item.unit_price))}
                                  value={draft.unitPrice}
                                  onChange={(e) => updateLine(item.po_item_id, { unitPrice: e.target.value })}
                                  inputProps={{ min: 0, step: '0.01' }}
                                  sx={{ minWidth: 100 }}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 110 }}>
                                <TextField
                                  size="small"
                                  value={draft.batchNumber}
                                  onChange={(e) => updateLine(item.po_item_id, { batchNumber: e.target.value })}
                                  sx={{ minWidth: 100 }}
                                />
                              </TableCell>
                              <TableCell sx={{ minWidth: 150 }}>
                                <TextField
                                  size="small"
                                  type="date"
                                  value={draft.expiryDate}
                                  onChange={(e) => updateLine(item.po_item_id, { expiryDate: e.target.value })}
                                  InputLabelProps={{ shrink: true }}
                                  sx={{ minWidth: 140 }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {blockingViolation && (
                  <Alert severity="error">
                    <AlertTitle>Over-receipt not allowed</AlertTitle>
                    One or more lines would receive more than the ordered quantity, and this tenant's match
                    tolerances do not allow over-receipt. Reduce the quantity or enable "Allow Over-Receipt" in
                    Match Tolerances first — the server will reject this with a 409 otherwise.
                  </Alert>
                )}

                <TextField
                  size="small"
                  fullWidth
                  multiline
                  minRows={2}
                  label="Remarks"
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {submitting ? 'Creating…' : 'Create Goods Receipt'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GoodsReceiptFormDialog;
